/**
 * The arithmetic behind the customer page's header and usage charts
 * (docs/engineering/admin-customer-insights.md): windows over the zero-filled days, the trailing
 * average, the weekday-by-hour grid, and day and month words. Pure, so it is tested without a DOM.
 */
import type { VolumeDay, VolumeHour } from '../types';

/** Pages over the `n` days that end `skip` days before the last one: (30, 0) is the last 30, (30, 30) the 30 before. */
export function windowSum(days: readonly VolumeDay[], n: number, skip = 0): number {
  const end = days.length - skip;
  return days.slice(Math.max(0, end - n), Math.max(0, end)).reduce((sum, d) => sum + d.pages, 0);
}

/** The trailing mean over `window` values; undefined until a full window has passed. */
export function trailingMean(values: readonly number[], window = 7): (number | undefined)[] {
  let running = 0;
  return values.map((v, i) => {
    running += v - (i >= window ? (values[i - window] ?? 0) : 0);
    return i >= window - 1 ? running / window : undefined;
  });
}

/** Monday first, as the ISO weekday the API sends (1 Monday … 7 Sunday). */
export const weekdayWords = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** The hours as a 7 × 24 grid of pages, zero where the API sent no cell. */
export function hourGrid(hours: readonly VolumeHour[]): number[][] {
  const grid = weekdayWords.map(() => Array.from({ length: 24 }, () => 0));
  for (const h of hours) {
    const row = grid[h.weekday - 1];
    if (row && h.hour >= 0 && h.hour < 24) row[h.hour] = h.pages;
  }
  return grid;
}

/** "9 am", "12 pm", "midnight": an hour of the day in words a salesperson reads. */
export function hourWord(hour: number): string {
  if (hour === 0) return 'midnight';
  if (hour === 12) return 'noon';
  return hour < 12 ? `${hour} am` : `${hour - 12} pm`;
}

/** The index of the largest value; the latest one on a tie, -1 for nothing above zero. */
export function peakIndex(values: readonly number[]): number {
  let best = -1;
  values.forEach((v, i) => {
    if (v > 0 && (best < 0 || v >= (values[best] ?? 0))) best = i;
  });
  return best;
}

/** A yyyy-MM-dd day in words ("Sep 28", or "Sep 28, 2026" with the year), read as a calendar day, never shifted by a zone. */
export function dayWord(day: string, locale: string, withYear = false): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: withYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
}

/** A yyyy-MM month in words: "Sep", or "Sep 2026" with the year. */
export function monthWord(month: string, locale: string, withYear = false): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    year: withYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`));
}

/** The weekday of a yyyy-MM-dd day, 1 Monday … 7 Sunday. */
export function isoWeekday(day: string): number {
  const d = new Date(`${day}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

/** One decimal, or none when it is ".0": 2 → "2", 2.5 → "2.5". */
const oneDecimal = (n: number, whole: boolean): string =>
  whole ? n.toFixed(0) : n.toFixed(1).replace(/\.0$/, '');

/**
 * 1,200 → "1.2k", 2,000 → "2k" (round 3, dataviz P4: never "2.0k"), 18,240 → "18k"; small numbers as
 * they are. For axes, never for the numbers people read.
 */
export function compact(n: number): string {
  if (n >= 1_000_000) return `${oneDecimal(n / 1_000_000, n >= 10_000_000)}M`;
  if (n >= 1000) return `${oneDecimal(n / 1000, n >= 10_000)}k`;
  return String(Math.round(n));
}

/** Pages a week for the last `weeks` whole weeks counted back from the last day: a sparkline without the weekday comb. */
export function weeklySums(days: readonly VolumeDay[], weeks: number): number[] {
  const out: number[] = [];
  for (let w = weeks - 1; w >= 0; w -= 1) {
    const end = days.length - w * 7;
    if (end - 7 < 0) continue;
    out.push(days.slice(end - 7, end).reduce((s, d) => s + d.pages, 0));
  }
  return out;
}
